import React, { useEffect, useState, useContext } from "react";
import { AuthContext } from "../../context/AuthContext";
import { getUsers, deleteUser, BACKEND_URL, toggleUserEnabled } from "../../api/api";
import ImportarUsuarios from "./ImportarUsuarios";

/**
 * Componente de sección de gestión de usuarios.
 * Muestra lista de usuarios en vista de tarjetas o tabla con funcionalidad de búsqueda,
 * edición, eliminación y cambio de estado (habilitado/deshabilitado).
 * 
 * @param {Object} props
 * @param {Function} props.onSelectUser - Callback al seleccionar un usuario
 * @param {Function} props.onEditUser - Callback al editar un usuario
 * @returns {JSX.Element} Sección de usuarios con lista y controles
 */
export default function UsersSection({ onSelectUser, onEditUser }) {
  const { token, user: currentUser } = useContext(AuthContext);
  const [usuarios, setUsuarios] = useState([]);
  const [usuariosFiltrados, setUsuariosFiltrados] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [busqueda, setBusqueda] = useState("");
  const [viewMode, setViewMode] = useState("grid"); // "grid" o "table"
  const [mostrarImportar, setMostrarImportar] = useState(false);
  const [paginaActual, setPaginaActual] = useState(1);
  const USUARIOS_POR_PAGINA = 20;

  useEffect(() => {
    cargarUsuarios();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    // Filtrar usuarios por nombre, apellidos o email
    const filtrados = usuarios.filter((u) => {
      const textoCompleto = `${u.nombre} ${u.apellidos} ${u.email}`.toLowerCase();
      return textoCompleto.includes(busqueda.toLowerCase());
    });
    setUsuariosFiltrados(filtrados);
    // Reset página al cambiar búsqueda o usuarios
    setPaginaActual(1);
  }, [busqueda, usuarios]);

  const cargarUsuarios = async () => {
    const res = await getUsers(token);
    setUsuarios(res);
  };

  const handleDelete = async (id) => {
    await deleteUser(token, id);
    setConfirmDelete(null);
    cargarUsuarios();
  };

  // Cálculo de paginación
  const totalPaginas = Math.ceil(usuariosFiltrados.length / USUARIOS_POR_PAGINA);
  const indiceInicio = (paginaActual - 1) * USUARIOS_POR_PAGINA;
  const indiceFin = indiceInicio + USUARIOS_POR_PAGINA;
  const usuariosPaginados = usuariosFiltrados.slice(indiceInicio, indiceFin);

  const irAPagina = (pagina) => {
    setPaginaActual(Math.max(1, Math.min(pagina, totalPaginas)));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="p-4 md:p-0">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Lista de Usuarios</h1>
        
        {/* Botones de acción */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => setMostrarImportar(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[#A7BC30] text-white rounded-lg hover:bg-[#8fa028] transition-colors font-medium text-sm md:text-base"
          >
            <i className="fas fa-file-excel"></i>
            <span className="hidden sm:inline">Importar Excel</span>
          </button>
          
          {/* Botones de cambio de vista */}
          <div className="flex items-center gap-2 bg-white rounded-lg p-1 shadow border border-gray-200">
          <button
            onClick={() => setViewMode("grid")}
            className={`flex items-center gap-2 px-3 py-2 rounded-md transition ${
              viewMode === "grid"
                ? "bg-gray-800 text-white"
                : "text-gray-600 hover:bg-gray-100"
            }`}
            title="Vista en tarjetas"
          >
            <i className="fas fa-th text-sm"></i>
            <span className="hidden sm:inline text-sm font-medium">Tarjetas</span>
          </button>
          <button
            onClick={() => setViewMode("table")}
            className={`flex items-center gap-2 px-3 py-2 rounded-md transition ${
              viewMode === "table"
                ? "bg-gray-800 text-white"
                : "text-gray-600 hover:bg-gray-100"
            }`}
            title="Vista en tabla"
          >
            <i className="fas fa-list text-sm"></i>
            <span className="hidden sm:inline text-sm font-medium">Tabla</span>
          </button>
        </div>
        </div>
      </div>

      {/* BARRA DE BÚSQUEDA */}
      <div className="mb-6 bg-white p-3 md:p-4 rounded-xl shadow">
        <div className="relative">
          <i className="fas fa-search absolute left-3 md:left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm md:text-base"></i>
          <input
            type="text"
            placeholder="Buscar por nombre, apellidos o email..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            className="w-full pl-10 md:pl-12 pr-3 md:pr-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent text-sm md:text-base"
          />
        </div>
        {busqueda && (
          <p className="text-sm text-gray-600 mt-2 flex items-center gap-2">
            <i className="fas fa-info-circle text-[#A7BC30]"></i>
            Se encontraron <strong>{usuariosFiltrados.length}</strong> usuario(s)
          </p>
        )}
      </div>

      {/* Información de paginación */}
      {usuariosFiltrados.length > 0 && (
        <div className="mb-4 flex flex-col sm:flex-row justify-between items-center gap-2 text-sm text-gray-600">
          <div>
            Mostrando <strong>{indiceInicio + 1}</strong> a <strong>{Math.min(indiceFin, usuariosFiltrados.length)}</strong> de <strong>{usuariosFiltrados.length}</strong> usuarios
          </div>
          {totalPaginas > 1 && (
            <div className="text-xs">
              Página <strong>{paginaActual}</strong> de <strong>{totalPaginas}</strong>
            </div>
          )}
        </div>
      )}

      {/* CARDS*/}
      {viewMode === "grid" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {usuariosPaginados.map((u) => (
          <div 
            key={u._id} 
            className="bg-white rounded-xl shadow hover:shadow-xl transition-all duration-300 cursor-pointer border border-gray-100 hover:border-gray-300 overflow-hidden group"
            onClick={() => onSelectUser?.(u._id)}
          >
            {/* Header con imagen de fondo */}
            <div className="relative h-24 bg-gray-700">
              <div className="absolute inset-0 bg-black opacity-5"></div>
            </div>

            {/* Avatar centrado sobre el header */}
            <div className="relative px-4 md:px-5 pb-4 md:pb-5">
              <div className="flex justify-center -mt-10 md:-mt-12 mb-3 md:mb-4">
                <img
                  src={
                    u.imagenPerfil
                      ? `${BACKEND_URL}${u.imagenPerfil}`
                      : `https://ui-avatars.com/api/?name=${encodeURIComponent(u.nombre + " " + u.apellidos)}&background=4F46E5&color=fff&size=128`
                  }
                  alt={u.nombre}
                  className="w-20 h-20 md:w-24 md:h-24 rounded-full object-cover border-4 border-white shadow-lg"
                />
              </div>

              {/* Info del usuario */}
              <div className="text-center mb-4">
                {/* Nombre completo */}
                <h3 className="font-bold text-base md:text-lg text-gray-800 mb-1.5">
                  {u.nombre} {u.segundoNombre && `${u.segundoNombre} `}
                  {u.primerApellido} {u.segundoApellido && u.segundoApellido}
                </h3>
                
                {/* Email */}
                <p className="text-xs md:text-sm text-gray-600 mb-3 truncate px-2">
                  <i className="fas fa-envelope mr-1 text-gray-400"></i>
                  {u.email}
                </p>
                
                {/* Información de contacto en grid */}
                <div className="grid grid-cols-2 gap-2 mb-3 px-2">
                  {u.dni && (
                    <div className="bg-gray-50 rounded-lg p-2">
                      <p className="text-xs text-gray-500 mb-0.5">
                        <i className="fas fa-id-card mr-1"></i>DNI
                      </p>
                      <p className="text-xs font-semibold text-gray-700">{u.dni}</p>
                    </div>
                  )}
                  {u.telefono && (
                    <div className="bg-gray-50 rounded-lg p-2">
                      <p className="text-xs text-gray-500 mb-0.5">
                        <i className="fas fa-phone mr-1"></i>Teléfono
                      </p>
                      <p className="text-xs font-semibold text-gray-700">{u.telefono}</p>
                    </div>
                  )}
                </div>

                {/* Empresa */}
                {u.empresa && (
                  <div className="bg-gray-100 rounded-lg p-2 mb-3 mx-2">
                    <p className="text-xs text-gray-700 font-medium">
                      <i className="fas fa-building mr-1 text-[#A7BC30]"></i>
                      {u.empresa.nombre}
                    </p>
                  </div>
                )}
                
                {/* Badges de rol y estado */}
                <div className="flex items-center justify-center gap-2 flex-wrap px-2">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                    <i className={`fas ${u.role === 'admin' ? 'fa-user-shield' : u.role === 'global_admin' ? 'fa-crown' : 'fa-user'}`}></i>
                    {u.role === 'admin' ? 'Admin' : u.role === 'global_admin' ? 'Global Admin' : 'Trabajador'}
                  </span>
                  
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
                    u.habilitado 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-gray-100 text-gray-500'
                  }`}>
                    <i className={`fas ${u.habilitado ? 'fa-check-circle' : 'fa-times-circle'}`}></i>
                    {u.habilitado ? 'Activo' : 'Deshabilitado'}
                  </span>
                </div>
              </div>

              {/* Separador */}
              <div className="border-t border-gray-100 mb-4"></div>

              {/* Acciones */}
              <div className="flex gap-1.5 md:gap-2">
                <button 
                  onClick={(e) => { 
                    e.stopPropagation(); 
                    onEditUser && onEditUser(u); 
                  }} 
                  className="flex-1 flex items-center justify-center gap-1 md:gap-2 bg-yellow-50 text-yellow-600 hover:bg-yellow-100 px-2 md:px-3 py-2 rounded-lg transition text-xs md:text-sm font-medium"
                  title="Editar"
                >
                  <i className="fas fa-edit"></i>
                  <span className="hidden sm:inline">Editar</span>
                </button>

                <button
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (currentUser?._id === u._id) return;
                    try {
                      await toggleUserEnabled(token, u._id, !u.habilitado);
                      cargarUsuarios();
                    } catch (err) {
                      alert(err.response?.data?.message || "Error cambiando estado de usuario");
                    }
                  }}
                  className={`flex items-center justify-center w-8 h-8 md:w-10 md:h-10 rounded-lg transition text-sm md:text-base ${
                    currentUser?._id === u._id 
                      ? 'bg-gray-100 text-gray-300 cursor-not-allowed' 
                      : u.habilitado
                        ? 'bg-green-50 text-green-600 hover:bg-green-100'
                        : 'bg-gray-50 text-gray-400 hover:bg-gray-100'
                  }`}
                  disabled={currentUser?._id === u._id}
                  title={currentUser?._id === u._id ? 'No puedes deshabilitarte a ti mismo' : (u.habilitado ? 'Deshabilitar usuario' : 'Habilitar usuario')}
                >
                  <i className={`fas ${u.habilitado ? 'fa-toggle-on' : 'fa-toggle-off'}`}></i>
                </button>

                <button 
                  onClick={(e) => { 
                    e.stopPropagation(); 
                    if (currentUser?._id === u._id) return;
                    setConfirmDelete(u._id); 
                  }} 
                  className={`flex items-center justify-center w-8 h-8 md:w-10 md:h-10 rounded-lg transition text-sm md:text-base ${
                    currentUser?._id === u._id
                      ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
                      : 'bg-red-50 text-red-600 hover:bg-red-100'
                  }`}
                  disabled={currentUser?._id === u._id}
                  title={currentUser?._id === u._id ? 'No puedes eliminarte a ti mismo' : 'Eliminar'}
                >
                  <i className="fas fa-trash-alt"></i>
                </button>
              </div>
            </div>
          </div>
        ))}
        </div>
      )}

      {/* TABLA */}
      {viewMode === "table" && (
        <>
          {/* Mensaje para móviles */}
          <div className="bg-blue-50 border border-blue-200 text-blue-700 px-3 py-2 rounded-lg mb-3 md:hidden text-sm flex items-center gap-2">
            <i className="fas fa-info-circle"></i>
            <span>Desliza horizontalmente para ver más datos</span>
          </div>
          
          <div className="bg-white rounded-xl shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[800px]">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-3 md:px-4 py-3 text-left text-xs md:text-sm font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">Usuario</th>
                    <th className="px-3 md:px-4 py-3 text-left text-xs md:text-sm font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">Contacto</th>
                    <th className="px-3 md:px-4 py-3 text-left text-xs md:text-sm font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">Empresa</th>
                    <th className="px-3 md:px-4 py-3 text-left text-xs md:text-sm font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">Rol</th>
                    <th className="px-3 md:px-4 py-3 text-left text-xs md:text-sm font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">Estado</th>
                    <th className="px-3 md:px-4 py-3 text-center text-xs md:text-sm font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">Acciones</th>
                  </tr>
                </thead>
              <tbody className="divide-y divide-gray-200">
                {usuariosPaginados.map((u) => (
                  <tr 
                    key={u._id} 
                    className="hover:bg-gray-50 transition cursor-pointer"
                    onClick={() => onSelectUser?.(u._id)}
                  >
                    {/* Usuario */}
                    <td className="px-3 md:px-4 py-3 md:py-4">
                      <div className="flex items-center gap-2 md:gap-3">
                        <img
                          src={
                            u.imagenPerfil
                              ? `${BACKEND_URL}${u.imagenPerfil}`
                              : `https://ui-avatars.com/api/?name=${encodeURIComponent(u.nombre + " " + u.apellidos)}&background=4F46E5&color=fff&size=64`
                          }
                          alt={u.nombre}
                          className="w-8 h-8 md:w-10 md:h-10 rounded-full object-cover border-2 border-gray-200 flex-shrink-0"
                        />
                        <div className="min-w-0">
                          <p className="font-semibold text-gray-800 text-xs md:text-sm truncate">
                            {u.nombre} {u.segundoNombre && `${u.segundoNombre} `}
                            {u.primerApellido} {u.segundoApellido && u.segundoApellido}
                          </p>
                          <p className="text-xs md:text-sm text-gray-500 truncate max-w-[150px] md:max-w-none">{u.email}</p>
                        </div>
                      </div>
                    </td>

                    {/* Contacto */}
                    <td className="px-3 md:px-4 py-3 md:py-4">
                      <div className="space-y-1">
                        {u.dni && (
                          <p className="text-xs md:text-sm text-gray-600">
                            <i className="fas fa-id-card mr-1 text-gray-400"></i>
                            {u.dni}
                          </p>
                        )}
                        {u.telefono && (
                          <p className="text-xs md:text-sm text-gray-600">
                            <i className="fas fa-phone mr-1 text-gray-400"></i>
                            {u.telefono}
                          </p>
                        )}
                      </div>
                    </td>

                    {/* Empresa */}
                    <td className="px-3 md:px-4 py-3 md:py-4">
                      {u.empresa ? (
                        <span className="inline-flex items-center gap-1 text-xs md:text-sm text-gray-700">
                          <i className="fas fa-building text-[#A7BC30]"></i>
                          {u.empresa.nombre}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>

                    {/* Rol */}
                    <td className="px-3 md:px-4 py-3 md:py-4">
                      <span className="inline-flex items-center gap-1.5 px-2 md:px-3 py-1 rounded-full text-xs md:text-sm font-medium bg-gray-100 text-gray-700 whitespace-nowrap">
                        <i className={`fas ${u.role === 'admin' ? 'fa-user-shield' : u.role === 'global_admin' ? 'fa-crown' : 'fa-user'}`}></i>
                        {u.role === 'admin' ? 'Admin' : u.role === 'global_admin' ? 'Global Admin' : 'Trabajador'}
                      </span>
                    </td>

                    {/* Estado */}
                    <td className="px-3 md:px-4 py-3 md:py-4">
                      <span className={`inline-flex items-center gap-1.5 px-2 md:px-3 py-1 rounded-full text-xs md:text-sm font-medium whitespace-nowrap ${
                        u.habilitado 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-gray-100 text-gray-500'
                      }`}>
                        <i className={`fas ${u.habilitado ? 'fa-check-circle' : 'fa-times-circle'}`}></i>
                        {u.habilitado ? 'Activo' : 'Deshabilitado'}
                      </span>
                    </td>

                    {/* Acciones */}
                    <td className="px-3 md:px-4 py-3 md:py-4">
                      <div className="flex items-center justify-center gap-1.5 md:gap-2">
                        <button 
                          onClick={(e) => { 
                            e.stopPropagation(); 
                            onEditUser && onEditUser(u); 
                          }} 
                          className="p-1.5 md:p-2 bg-yellow-50 text-yellow-600 hover:bg-yellow-100 rounded-lg transition text-sm md:text-base"
                          title="Editar"
                        >
                          <i className="fas fa-edit"></i>
                        </button>

                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (currentUser?._id === u._id) return;
                            try {
                              await toggleUserEnabled(token, u._id, !u.habilitado);
                              cargarUsuarios();
                            } catch (err) {
                              alert(err.response?.data?.message || "Error cambiando estado de usuario");
                            }
                          }}
                          className={`p-2 rounded-lg transition ${
                            currentUser?._id === u._id 
                              ? 'bg-gray-100 text-gray-300 cursor-not-allowed' 
                              : u.habilitado
                                ? 'bg-green-50 text-green-600 hover:bg-green-100'
                                : 'bg-gray-50 text-gray-400 hover:bg-gray-100'
                          }`}
                          disabled={currentUser?._id === u._id}
                          title={currentUser?._id === u._id ? 'No puedes deshabilitarte a ti mismo' : (u.habilitado ? 'Deshabilitar usuario' : 'Habilitar usuario')}
                        >
                          <i className={`fas ${u.habilitado ? 'fa-toggle-on' : 'fa-toggle-off'}`}></i>
                        </button>

                        <button 
                          onClick={(e) => { 
                            e.stopPropagation(); 
                            if (currentUser?._id === u._id) return;
                            setConfirmDelete(u._id); 
                          }} 
                          className={`p-2 rounded-lg transition ${
                            currentUser?._id === u._id
                              ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
                              : 'bg-red-50 text-red-600 hover:bg-red-100'
                          }`}
                          disabled={currentUser?._id === u._id}
                          title={currentUser?._id === u._id ? 'No puedes eliminarte a ti mismo' : 'Eliminar'}
                        >
                          <i className="fas fa-trash-alt"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        </>
      )}

      {/* Controles de paginación */}
      {totalPaginas > 1 && usuariosFiltrados.length > 0 && (
        <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white p-4 rounded-xl shadow">
          {/* Botón anterior */}
          <button
            onClick={() => irAPagina(paginaActual - 1)}
            disabled={paginaActual === 1}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              paginaActual === 1
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            <i className="fas fa-chevron-left"></i>
            <span className="hidden sm:inline">Anterior</span>
          </button>

          {/* Números de página */}
          <div className="flex items-center gap-2 flex-wrap justify-center">
            {/* Primera página */}
            {paginaActual > 3 && (
              <>
                <button
                  onClick={() => irAPagina(1)}
                  className="w-10 h-10 flex items-center justify-center rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition"
                >
                  1
                </button>
                {paginaActual > 4 && (
                  <span className="text-gray-400">...</span>
                )}
              </>
            )}

            {/* Páginas cercanas */}
            {[...Array(totalPaginas)].map((_, index) => {
              const numeroPagina = index + 1;
              
              // Mostrar solo páginas cercanas a la actual
              if (
                numeroPagina === paginaActual ||
                (numeroPagina >= paginaActual - 2 && numeroPagina <= paginaActual + 2)
              ) {
                return (
                  <button
                    key={numeroPagina}
                    onClick={() => irAPagina(numeroPagina)}
                    className={`w-10 h-10 flex items-center justify-center rounded-lg font-medium transition ${
                      numeroPagina === paginaActual
                        ? 'bg-[#A7BC30] text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {numeroPagina}
                  </button>
                );
              }
              return null;
            })}

            {/* Última página */}
            {paginaActual < totalPaginas - 2 && (
              <>
                {paginaActual < totalPaginas - 3 && (
                  <span className="text-gray-400">...</span>
                )}
                <button
                  onClick={() => irAPagina(totalPaginas)}
                  className="w-10 h-10 flex items-center justify-center rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition"
                >
                  {totalPaginas}
                </button>
              </>
            )}
          </div>

          {/* Botón siguiente */}
          <button
            onClick={() => irAPagina(paginaActual + 1)}
            disabled={paginaActual === totalPaginas}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              paginaActual === totalPaginas
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            <span className="hidden sm:inline">Siguiente</span>
            <i className="fas fa-chevron-right"></i>
          </button>
        </div>
      )}

      {usuariosFiltrados.length === 0 && busqueda && (
        <div className="text-center py-12">
          <i className="fas fa-search text-6xl text-gray-300 mb-4"></i>
          <p className="text-gray-500 text-lg">No se encontraron usuarios con esa búsqueda</p>
        </div>
      )}

      {confirmDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-4 md:p-6 rounded-xl shadow-2xl w-full max-w-md">
            <div className="flex items-center gap-2 md:gap-3 mb-4">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-red-100 rounded-full flex items-center justify-center">
                <i className="fas fa-exclamation-triangle text-red-600 text-lg md:text-xl"></i>
              </div>
              <h3 className="text-lg md:text-xl font-bold text-gray-800">
                Confirmar eliminación
              </h3>
            </div>
            <p className="text-sm md:text-base text-gray-600 mb-4 md:mb-6 leading-relaxed">
              ¿Estás seguro de que deseas eliminar este usuario? Esta acción <strong>no se podrá deshacer</strong>.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-end">
              <button 
                onClick={() => setConfirmDelete(null)} 
                className="bg-gray-100 text-gray-700 px-4 md:px-5 py-2.5 rounded-lg hover:bg-gray-200 transition font-medium text-sm md:text-base"
              >
                Cancelar
              </button>
              <button 
                onClick={() => handleDelete(confirmDelete)} 
                className="bg-red-600 text-white px-4 md:px-5 py-2.5 rounded-lg hover:bg-red-700 transition font-medium flex items-center justify-center gap-2 text-sm md:text-base"
              >
                <i className="fas fa-trash-alt"></i>
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de importar usuarios */}
      {mostrarImportar && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl my-8">
            <div className="flex justify-between items-center p-4 md:p-6 border-b">
              <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                Importar Usuarios desde Excel
              </h2>
              <button
                onClick={() => setMostrarImportar(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors text-2xl"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="p-4 md:p-6">
              <ImportarUsuarios 
                onImportSuccess={() => {
                  cargarUsuarios();
                }}
              />
            </div>
            <div className="flex justify-end p-4 md:p-6 border-t bg-gray-50 rounded-b-xl">
              <button
                onClick={() => setMostrarImportar(false)}
                className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
